import { useQuery } from 'react-query'
import axios from 'src/utils/axios'
import { API_BASE_URL } from 'src/constants'
import { fetchWithPartner, withPartnerParam } from 'src/api/partner'

// A tumour RNA analysis (roadmap R4.1, R4.3a): its library, specimen, links and
// RNA runs, and one page of its expression store. Every answer carries a state:
// an empty gene table is "none found" only when the state says evaluated.

const get = async (url, params) => {
  const { data } = await axios.get(url, { params })
  return data
}

const options = (awId, enabled, extra = {}) => ({
  enabled: enabled && !!awId,
  retry: false,
  keepPreviousData: true,
  staleTime: 15_000,
  ...extra,
})

// URLSearchParams carry a repeated `gene`; JSON.stringify would key them all "{}".
const paramsKey = (params) => (params instanceof URLSearchParams ? params.toString() : JSON.stringify(params))
export const rnaKey = (awId, kind, params = {}) => ['rna', awId, kind, paramsKey(params)]

// Polls while a run is still running, so the page follows the chain.
export const useRnaAnalysis = ({ awId, enabled = true }) =>
  useQuery(
    rnaKey(awId, 'summary'),
    () => get(`${API_BASE_URL}/rna/${awId}`),
    options(awId, enabled, {
      refetchInterval: (data) => ((data?.runs || [])[0]?.status === 'running' ? 15_000 : false),
    }),
  )

// `partner` on these picks the DNA/RNA pair (the other side's analysis); none
// means the primary pair.
export const useRnaExpression = ({ awId, params = {}, partner = null, enabled = true }) =>
  useQuery(
    rnaKey(awId, 'expression', withPartnerParam(params, partner)),
    () => fetchWithPartner((p) => get(`${API_BASE_URL}/rna/${awId}/expression`, p), params, partner),
    options(awId, enabled),
  )

// The DNA variants of the paired DNA analysis, each read in this RNA: expressed,
// not expressed, equivocal, not covered or not assessed (with the reason).
//
// The full answer holds every site of the analysis (several MB). A page that shows
// a few variants asks for those only:
// * `alleleIds` (the rows' allele ids): only their sites, with `not_read[]` per DNA
//   part naming the asked variants that were not read. A list too long for a query
//   string goes to the lookup route.
// * `view: 'summary'`: no sites at all, only each part's state, staleness and counts.
// A server without these options ignores them and serves the full answer, which
// every reader here still reads the same way.
const expressedUrl = (awId) => `${API_BASE_URL}/structural/${awId}/expressed-alleles`

// Ids as the key and the request name them: trimmed, unique, sorted.
export const normaliseAlleleIds = (ids) =>
  Array.from(new Set((ids || []).map((a) => String(a ?? '').trim()).filter(Boolean))).sort()

// Above this many characters the ids are sent in a POST body instead of the URL.
export const ALLELE_IDS_QUERY_MAX_CHARS = 3000

const isMissingRoute = (error) => [404, 405].includes(error?.response?.status ?? error?.status)

export const fetchExpressedAllelesFor = async (awId, alleleIds, partner = null) => {
  const ids = normaliseAlleleIds(alleleIds)
  const joined = ids.join(',')
  const getFiltered = () => fetchWithPartner((p) => get(expressedUrl(awId), p), { allele_ids: joined }, partner)
  if (joined.length <= ALLELE_IDS_QUERY_MAX_CHARS) return getFiltered()
  try {
    return await fetchWithPartner(
      async (p) => {
        const { data } = await axios.post(`${expressedUrl(awId)}/lookup`, { allele_ids: ids }, { params: p })
        return data
      },
      undefined,
      partner,
    )
  } catch (error) {
    // A server without the lookup route: the full answer (it ignores allele_ids).
    if (!isMissingRoute(error)) throw error
    return fetchWithPartner((p) => get(expressedUrl(awId), p), undefined, partner)
  }
}

export const useExpressedAlleles = ({ awId, partner = null, enabled = true, alleleIds = null, view = null }) => {
  const ids = Array.isArray(alleleIds) ? normaliseAlleleIds(alleleIds) : null
  const keyParams = {
    ...(partner ? { partner } : {}),
    ...(view ? { view } : {}),
    ...(ids ? { allele_ids: ids.join(',') } : {}),
  }
  return useQuery(
    rnaKey(awId, 'expressed-alleles', keyParams),
    () =>
      ids
        ? fetchExpressedAllelesFor(awId, ids, partner)
        : fetchWithPartner((p) => get(expressedUrl(awId), p), view ? { view } : undefined, partner),
    // An empty page asks for nothing.
    options(awId, enabled && (!ids || ids.length > 0)),
  )
}

// The splice-variant targets (R4.6: MET exon 14 skipping, EGFRvIII, AR-V7), each with
// its call or its state and reason.
export const useRnaSplice = ({ awId, partner = null, enabled = true }) =>
  useQuery(
    rnaKey(awId, 'splice', partner ? { partner } : {}),
    () => fetchWithPartner((p) => get(`${API_BASE_URL}/rna/${awId}/splice`, p), undefined, partner),
    options(awId, enabled),
  )

// The wizard's library vocabulary, and whether this environment can start an RNA
// analysis at all (`enabled`, else `reason`).
export const useRnaVocabulary = ({ enabled = true } = {}) =>
  useQuery(['rna', 'vocabulary'], () => get(`${API_BASE_URL}/rna-vocabulary`), {
    enabled,
    retry: false,
    staleTime: 60_000,
  })
